import { useState, useEffect } from 'react';
import { useEmpresasContext } from '../context/EmpresasContext';
import { AuthServiceUAU } from '../services/authServiceUAU';

export interface UsuarioUAU {
  id: number;
  username: string;
  email: string;
  full_name: string;
  is_active: boolean;
  tipo_usuario?: string;
}


export function useUsuariosUAU() {
  const [usuarios, setUsuarios] = useState<Record<number, UsuarioUAU>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { empresaActiva } = useEmpresasContext();

  useEffect(() => {
    const loadUsuarios = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        if (!empresaActiva?.empresa?.id) {
          setUsuarios({});
          return;
        }
        
        const token = AuthServiceUAU.getToken();
        if (!token) {
          throw new Error('No hay token de autenticación');
        }
        
        const response = await fetch('/mava/api/v1/uau/usuarios-disponibles', {
          headers: {
            'Authorization': `Bearer ${token}`,
            'X-Empresa-ID': empresaActiva.empresa.id.toString(),
            'Content-Type': 'application/json'
          }
        });
        
        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Error ${response.status}: ${response.statusText} - ${errorText}`);
        }
        
        const data: {
          usuarios_disponibles: UsuarioUAU[];
          organization_id: number;
          organization_name: string;
          total_disponibles: number;
          total_users_in_org: number;
          excluidos: {
            inactivos: number;
          };
          application_id: number;
        } = await response.json();
        
        // Convertir array a objeto indexado por ID
        const usuariosIndexados = data.usuarios_disponibles.reduce((acc, usuario) => {
          acc[usuario.id] = usuario;
          return acc;
        }, {} as Record<number, UsuarioUAU>);
        
        setUsuarios(usuariosIndexados);
        
      } catch (err: any) {
        setError(`Error cargando usuarios de UAU: ${err.message || 'Error desconocido'}`);
        setUsuarios({});
      } finally {
        setIsLoading(false);
      }
    };

    loadUsuarios();
  }, [empresaActiva?.empresa?.id]);

  // Función para obtener usuario por ID
  const getUsuarioById = (id: number): UsuarioUAU | undefined => {
    return usuarios[id];
  };

  // Función para obtener nombre del usuario
  const getUserDisplayName = (id: number): string => {
    const usuario = usuarios[id];
    if (!usuario) return `Usuario ${id}`;
    
    return usuario.full_name || usuario.username || `Usuario ${id}`;
  };

  // Función para obtener usuarios activos
  const getUsuariosActivos = (): UsuarioUAU[] => {
    return Object.values(usuarios).filter(u => u.is_active);
  };

  // Función para buscar usuarios
  const searchUsuarios = (query: string): UsuarioUAU[] => {
    const lowerQuery = query.toLowerCase();
    return Object.values(usuarios).filter(u => 
      u.full_name?.toLowerCase().includes(lowerQuery) ||
      u.username.toLowerCase().includes(lowerQuery) ||
      u.email.toLowerCase().includes(lowerQuery)
    );
  };

  return {
    usuarios,
    isLoading,
    error,
    getUsuarioById,
    getUserDisplayName,
    getUsuariosActivos,
    searchUsuarios,
  };
}
